import { canOpenTemplatePopup } from "../../components/DocsTab/DocsTabTable/esignTemplates"
import ContainerPage from "../../components/ContainerPage/ContainerPage"
import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getContactById } from "../../features/customer/customerSlice"
import {
  LuFileText, LuWallet, LuArrowLeftRight, LuStickyNote, LuCreditCard,
  LuFileBarChart, LuMail, LuMessageSquare, LuListChecks, LuHistory,
} from "react-icons/lu"
import Tab from "../../components/Tab/Tab"
import PaymentTab from "../../components/PaymentTab/PaymentTab"
import PaymentMethodTab from "../../components/PaymentMethodTab/PaymentMethodTab"
import DocsTab from "../../components/DocsTab/DocsTab"
import CreditReports from "../../components/CreditReports/CreditReports"
import { MyContextProvider } from "../../components/MyContextProvider/MyContextProvider"
import FloatingPopup from "../../components/Modal/FloatingPopup"
import EsignTemplate from "../Esign/EsignTemplate"
import EmailsTab from "../../components/EmailsTab/EmailsTab"
import SmsTab from "../../components/SmsTab/SmsTab"
import SideBarInfo from "./SideBarInfo"
import TasksTab from "../../components/TasksTab/TasksTab"
import { getDebtsByUser } from "../../features/creditReports/creditReports"
import { StorageGetItem, USER } from "../../lib/storage"
import { useIsSupervisor } from "../../hooks/user"
import { useLocation, useParams } from 'react-router-dom'
import Notes from "../Notes/Notes"
import History from "../History/History"
import ContactProfile from "./contactProfile"
import Transactions from "../../components/Transactions/Transactions"
import AccountAlerts from "../../components/AccountAlerts"
import DuplicateBanner from "../../components/DuplicateBanner/DuplicateBanner"
import { TABLE_CARD } from "../../components/Table/tableCardClass"
import { openedFromBell } from "../../features/notifications/links"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { AFFILIATE_TASKS_UI, CONTACT_FINANCING, CONTACT_FINANCING_GROUP_ADMIN, CONTACT_FINANCING_PENDING, DASHBOARD_DRILLDOWN_LINKS, ESIGN_DOCS_SIGNING_LINK, ESIGN_LSA_PAYMENT_UPFRONT, PCA_FOR_AFFILIATES } from "../../config/featureFlagKeys"

const Contact = () => {
  const dispatch = useDispatch()
  const savedActiveTab = localStorage.getItem('activeTab')
  const isSupervisor = useIsSupervisor()
  const financingEnabled = useSelector(selectFeatureFlag(CONTACT_FINANCING))
  const financingPendingEnabled = useSelector(selectFeatureFlag(CONTACT_FINANCING_PENDING))
  // WS 2346314. The server holds a group admin to marked files and the pending fee.
  const financingGroupAdmin = useSelector(selectFeatureFlag(CONTACT_FINANCING_GROUP_ADMIN))
  const pcaForAffiliates = useSelector(selectFeatureFlag(PCA_FOR_AFFILIATES))
  const signingLinkOn = useSelector(selectFeatureFlag(ESIGN_DOCS_SIGNING_LINK))
  const lsaChargeNameOn = useSelector(selectFeatureFlag(ESIGN_LSA_PAYMENT_UPFRONT))
  const affiliateTasksOn = useSelector(selectFeatureFlag(AFFILIATE_TASKS_UI))
  // Remounts the Payment tab so it re-reads the balance after financing is recorded.
  const [financingVersion, setFinancingVersion] = useState(0)
  const location = useLocation()
  const drilldownLinks = useSelector(selectFeatureFlag(DASHBOARD_DRILLDOWN_LINKS))
  const backTo = drilldownLinks && location.key === 'default' ? '/customers' : undefined

  const { contactId } = useParams()
  const [userData, setUserData] = useState("")
  const [activeTab, setActiveTab] = useState(savedActiveTab || 'docs')
  const [isPaymentDisabled, setIsPaymentDisabled] = useState(true)
  const [triggerUpdateSidebar, setTriggerUpdateSidebar] = useState(false)
  const [alertRefreshTrigger, setAlertRefreshTrigger] = useState(0)

  // --- record tab strip (horizontal scroll) UX ---
  const tabStripRef = useRef(null)
  // Keep the active tab visible when it starts off-screen (e.g. Tasks/History far right).
  // Set scrollLeft directly (never scrollIntoView) so the page can't jump vertically.
  // Depend on userData too so it re-runs once the (async) profile renders the strip.
  useLayoutEffect(() => {
    const strip = tabStripRef.current
    if (!strip) return
    const active = strip.querySelector('[data-active="true"]')
    if (!active) return
    const PAD = 24
    const sr = strip.getBoundingClientRect()
    const ar = active.getBoundingClientRect()
    const t = ar.left - sr.left + strip.scrollLeft   // active tab's left within the scroll content
    const w = ar.width
    const s = strip.scrollLeft, vw = strip.clientWidth
    let target = s
    if (t < s + PAD) target = t - PAD
    else if (t + w > s + vw - PAD) target = t + w - vw + PAD
    if (Math.abs(target - s) > 1) strip.scrollTo({ left: Math.max(0, target), behavior: 'auto' })
  }, [activeTab, userData])
  // Edge fades: show only on the side(s) that still have tabs to reveal.
  useEffect(() => {
    const strip = tabStripRef.current
    if (!strip) return
    const update = () => {
      const over = strip.scrollWidth > strip.clientWidth + 1
      const atStart = strip.scrollLeft <= 1
      const atEnd = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 1
      strip.dataset.overflow = !over ? 'none' : atStart ? 'end' : atEnd ? 'start' : 'both'
    }
    update()
    strip.addEventListener('scroll', update, { passive: true })
    // The strip's own box never changes when tabs are added, only its scrollWidth, so a
    // ResizeObserver alone never re-fires: it caught sidebar collapse / viewport resize but
    // not the tab set filling in. The tabs are gated on userRole + userData, which resolve
    // asynchronously *after* mount, so the single on-mount measurement always ran against an
    // empty strip and pinned data-overflow to "none" — the fade could never appear.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
    if (ro) ro.observe(strip)
    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(update) : null
    if (mo) mo.observe(strip, { childList: true, subtree: true, characterData: true })
    return () => {
      strip.removeEventListener('scroll', update)
      if (ro) ro.disconnect()
      if (mo) mo.disconnect()
    }
  }, [])

  const [templateData, setTemplateData] = useState({ status: false, view: 'hide' })
  const [noteCreateTrigger, setNoteCreateTrigger] = useState([])

  const [templateDataAction, setTemplateDataAction] = useState({ isReady: false, deliveryMethod: 'email' })
  const [userRole, setUserRole] = useState('')
  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
    })
  }, [])

  useEffect(() => {
    const tabFromState = location?.state?.openTab

    const params = new URLSearchParams(location.search)
    const tabFromQuery = params.get('activeTab')

    if (tabFromQuery) {
      setActiveTab(tabFromQuery)
      localStorage.setItem('activeTab', tabFromQuery)
    } else if (tabFromState) {
      setActiveTab(tabFromState)
      localStorage.setItem('activeTab', tabFromState)
    }
  }, [location])

  const landedFromBell = useRef(openedFromBell(location.search))

  const askedForTasks = useRef(
    new URLSearchParams(location.search).get('activeTab') === 'tasks' || location.state?.openTab === 'tasks'
  )

  useEffect(() => {
    userRole === 'affiliate' && !landedFromBell.current && handleTabChange('payment')
  }, [userRole])

  useEffect(() => {
    if (userRole === 'affiliate' && !landedFromBell.current && askedForTasks.current && affiliateTasksOn) {
      handleTabChange('tasks')
      askedForTasks.current = false
    }
  }, [userRole, affiliateTasksOn])


  useEffect(() => {
    localStorage.setItem('activeTab', activeTab)
  }, [activeTab])

  const handleTabChange = (tab) => {
    setActiveTab(tab)
  }

  const fetchData = async () => {
    try {
      const [resultAction, response] = await Promise.all([
        dispatch(getContactById(contactId)),
        dispatch(getDebtsByUser(contactId)),
      ])

      if (getContactById.fulfilled.match(resultAction)) {
        setUserData(resultAction.payload.data)
      } else {
        console.error('Error fetching contact:', resultAction.payload)
      }

      const reports = response.payload.data
      if (response.payload && Array.isArray(reports) && reports?.length > 0) {
        setIsPaymentDisabled(false)
      } else {
        console.error('No data received from server')
      }
    } catch (error) {
      console.error('Error dispatching getContactById:', error)
    }
  }

  useEffect(() => {
    setUserData("")
    setIsPaymentDisabled(true)
    fetchData()
    userRole === 'affiliate' && setIsPaymentDisabled(false)
  }, [contactId])

  return (
    <MyContextProvider key={contactId} triggerData={setTemplateData}>
      {templateData?.status &&
        canOpenTemplatePopup({ userRole, isSupervisor, typeDoc: templateData?.typeDoc, pcaForAffiliates }) &&
        <FloatingPopup setProceed={setTemplateDataAction} templateData={templateData} userId={contactId} signingLinkOn={signingLinkOn} />}
      <ContainerPage>
        {(!templateDataAction?.isReady) ?
          <>
            <DuplicateBanner user={userData} />
            {/* Account Alerts - displayed below profile header */}
            <div className="mt-4">
              <AccountAlerts userId={contactId} externalRefreshTrigger={alertRefreshTrigger} />
            </div>
            <ContactProfile user={userData} userId={contactId} triggerUpdateSidebar={triggerUpdateSidebar} userRole={userRole} onAlertSaved={() => setAlertRefreshTrigger(prev => prev + 1)}
              financingEnabled={financingEnabled}
              financingPendingEnabled={financingPendingEnabled}
              lsaChargeNameEnabled={lsaChargeNameOn}
              backTo={backTo}
              canRecordFinancing={userRole === 'customer' || isSupervisor || (financingGroupAdmin && userRole === 'affiliateGroupsAdmin')}
              onFinancingRecorded={() => {
                fetchData()
                setTriggerUpdateSidebar(prev => !prev)
                setFinancingVersion(prev => prev + 1)
              }} />

            <div className="mt-4 gap-6 flex w-full">
              <SideBarInfo userId={contactId} triggerUpdateSidebar={triggerUpdateSidebar}
                setNoteCreateTrigger={setNoteCreateTrigger} />
              <div className={`${TABLE_CARD} flex-1 min-w-0`}>
                <div
                  ref={tabStripRef}
                  className="tab-scroll px-3 flex gap-1 mb-6 bg-surface w-full rounded-t-2xl border-b border-solid border-divider overflow-x-auto overscroll-x-contain [&>*]:shrink-0 [&>*:last-child]:me-6">
                  <Tab classes="text-md"
                    imgLink={<LuFileText size={18} color={activeTab === "docs" ? 'var(--color-primary-main)' : 'var(--color-gray-600)'} />}
                    isActive={activeTab === "docs"}
                    onClick={() => handleTabChange('docs')}>
                    Docs
                  </Tab>
                  <Tab classes="text-md"
                    imgLink={<LuWallet size={18} color={activeTab === "payment" ? 'var(--color-primary-main)' : 'var(--color-gray-600)'} />}
                    isActive={activeTab === "payment"} onClick={() => handleTabChange('payment')}
                    disabled={isPaymentDisabled}>
                    Payment
                  </Tab>
                  {(userRole === 'customer' || userRole === 'user') && <Tab classes="text-md"
                    imgLink={<LuArrowLeftRight size={18} color={activeTab === "transactions" ? 'var(--color-primary-main)' : 'var(--color-gray-600)'} />}
                    isActive={activeTab === "transactions"}
                    onClick={() => handleTabChange('transactions')}>
                    Transactions
                  </Tab>}
                  <Tab classes="text-md"
                    imgLink={<LuStickyNote size={18} color={activeTab === "notes" ? 'var(--color-primary-main)' : 'var(--color-gray-600)'} />}
                    isActive={activeTab === "notes"}
                    onClick={() => handleTabChange('notes')}
                  >
                    Notes
                  </Tab>

                  {(userRole === 'customer' || userRole === 'user') && <Tab classes="text-md"
                    imgLink={<LuCreditCard size={18} color={activeTab === "billing" ? 'var(--color-primary-main)' : 'var(--color-gray-600)'} />}
                    isActive={activeTab === "billing"}
                    onClick={() => handleTabChange('billing')}>
                    Billing
                  </Tab>}
                  <Tab classes="text-md"
                    imgLink={<LuFileBarChart size={18} color={activeTab === "creditReports" ? 'var(--color-primary-main)' : 'var(--color-gray-600)'} />}
                    isActive={activeTab === "creditReports"}
                    onClick={() => handleTabChange('creditReports')}>
                    Credit Report
                  </Tab>
                  {(userRole === 'customer' || userRole === 'user') && <Tab classes="text-md"
                    imgLink={<LuMail size={18} color={activeTab === "emails" ? 'var(--color-primary-main)' : 'var(--color-gray-600)'} />}
                    isActive={activeTab === "emails"}
                    onClick={() => handleTabChange('emails')}>
                    Emails
                  </Tab>}
                  {(userRole === 'customer' || userRole === 'user') && <Tab classes="text-md"
                    imgLink={<LuMessageSquare size={18} color={activeTab === "sms" ? 'var(--color-primary-main)' : 'var(--color-gray-600)'} />}
                    isActive={activeTab === "sms"}
                    onClick={() => handleTabChange('sms')}>
                    Sms
                  </Tab>}
                  {(userRole === 'customer' || userRole === 'user' || (affiliateTasksOn && (userRole === 'affiliate' || userRole === 'affiliateGroupsAdmin'))) && <Tab classes="text-md"
                    imgLink={<LuListChecks size={18} color={activeTab === "tasks" ? 'var(--color-primary-main)' : 'var(--color-gray-600)'} />}
                    isActive={activeTab === "tasks"}
                    onClick={() => handleTabChange('tasks')}>
                    Tasks
                  </Tab>}
                  {(userRole === 'customer' || userRole === 'user') && <Tab classes="text-md"
                    imgLink={<LuHistory size={18} color={activeTab === "history" ? 'var(--color-primary-main)' : 'var(--color-gray-600)'} />}
                    isActive={activeTab === "history"}
                    onClick={() => handleTabChange('history')}>
                    History
                  </Tab>}
                </div>
                <div className="px-5 pb-6">
                  {activeTab === 'payment' && <PaymentTab key={financingVersion} setTriggerUpdateSidebar={setTriggerUpdateSidebar} />}
                  {activeTab === 'transactions' && <Transactions userRole={userRole} />}
                  {activeTab === 'billing' && <PaymentMethodTab userId={contactId} userData={userData}
                    setTriggerUpdateSidebar={setTriggerUpdateSidebar} />}
                  {activeTab === 'creditReports' && <CreditReports />}
                  {activeTab === 'docs' && <DocsTab userId={contactId} contact={userData} signingLinkOn={signingLinkOn} />}
                  {activeTab === 'emails' && <EmailsTab userId={contactId} userData={userData} />}
                  {activeTab === 'sms' && <SmsTab userId={contactId} userData={userData} />}
                  {activeTab === 'tasks' && <TasksTab userId={contactId} userData={userData} />}
                  {activeTab === 'notes' && <Notes userId={contactId} userData={userData}
                    setTriggerUpdateSidebar={setTriggerUpdateSidebar}
                    noteCreateTrigger={noteCreateTrigger} />}
                  {activeTab === 'history' && <History userId={contactId} />}
                </div>

              </div>
            </div>
            {(templateData?.status && templateData?.view === 'hide') &&
              <div className="fixed bottom-0 right-1 bg-gray-950 flex items-center px-3 rounded-t-lg">
                <p className="text-white font-fontFamily text-ms mr-6 font-bold">Missing fields</p>
                <div className="flex items-center">
                  <svg onClick={() => setTemplateData({ ...templateData, view: 'show' })}
                    className="cursor-pointer mr-2" xmlns="http://www.w3.org/2000/svg" width="12" height="12"
                    viewBox="0 0 12 12" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd"
                      d="M6.5625 0.84375C6.5625 0.37776 6.94026 0 7.40625 0H11.1563C11.6222 0 12 0.37776 12 0.84375V4.59375C12 5.05974 11.6222 5.4375 11.1563 5.4375C10.6903 5.4375 10.3125 5.05974 10.3125 4.59375V2.88074L2.88074 10.3125H4.59375C5.05974 10.3125 5.4375 10.6903 5.4375 11.1563C5.4375 11.6222 5.05974 12 4.59375 12H0.84375C0.37776 12 0 11.6222 0 11.1563V7.40625C0 6.94026 0.37776 6.5625 0.84375 6.5625C1.30974 6.5625 1.6875 6.94026 1.6875 7.40625V9.11926L9.11926 1.6875H7.40625C6.94026 1.6875 6.5625 1.30974 6.5625 0.84375Z"
                      fill="white" />
                  </svg>

                  <svg onClick={() => setTemplateData({ status: false, view: 'hide' })} className="cursor-pointer"
                    xmlns="http://www.w3.org/2000/svg" width="40" height="40"
                    viewBox="0 0 40 40" fill="none">
                    <path
                      d="M21.4032 19.9937L25.6976 15.709C26.0892 15.3174 26.0892 14.6824 25.6976 14.2908C25.306 13.8992 24.671 13.8992 24.2794 14.2908L19.995 18.5855L15.7106 14.2908C15.319 13.8992 14.6841 13.8992 14.2925 14.2908C13.9009 14.6824 13.9009 15.3174 14.2925 15.709L18.5869 19.9937L14.2925 24.2784C14.1034 24.466 13.9971 24.7213 13.9971 24.9876C13.9971 25.2539 14.1034 25.5092 14.2925 25.6967C14.48 25.8858 14.7353 25.9921 15.0016 25.9921C15.2678 25.9921 15.5231 25.8858 15.7106 25.6967L19.995 21.402L24.2794 25.6967C24.4669 25.8858 24.7222 25.9921 24.9885 25.9921C25.2548 25.9921 25.51 25.8858 25.6976 25.6967C25.8866 25.5092 25.993 25.2539 25.993 24.9876C25.993 24.7213 25.8866 24.466 25.6976 24.2784L21.4032 19.9937Z"
                      fill="white" />
                  </svg>
                </div>
              </div>
            }
          </> :
          <EsignTemplate userId={contactId} templateDataAction={templateDataAction}
            isOnboarding={false}
            cancel={() => setTemplateDataAction(prev => ({ ...prev, isReady: false }))} />
        }
      </ContainerPage>
    </MyContextProvider>
  )
}

export default Contact

